import Phaser from "phaser";
import LevelBase from "./levelBase";
import { Empty, Exit, Interactive, NPCharacter, Player } from "../objects";
import EasyStar from "easystarjs";
import Debris from "../objects/Debris";
import { PIXEL_NAME } from "../../utils/constants";
import { translate } from "../../utils/localization";
import template from 'lodash.template'

class LevelMaze1 extends LevelBase {
  constructor() {
    super();
    this.name = "The Maze";
    this.easystar = new EasyStar.js();
    this.solvingPath = [];
    this.isMaze = true;
    this.exitEye = null;
    this.dwarfRun = false;
    this.buyLeadingWithShrooms = false;
  }

  async connectToScene(scene) {
    await super.connectToScene(scene);
    //  Enable lights and set a dark ambient color
    scene.lights.enable().setAmbientColor(0x000000);

    // this.exit = Object.values(this.interactiveElements).find((el) =>
    //   el.isClass(Exit)
    // );

    // Path finder to exit, runs alwas since is used to detect unreachable exit
    const objectsAsKeys = this.objects.map((row) =>
      row.map((el) => (el.isClass(NPCharacter) || el.isClass(Empty) ? 0 : 1))
    );
    this.easystar.setGrid(objectsAsKeys);
    this.easystar.setAcceptableTiles([0]);

    this.easystar.findPath(
      this.leader.position.x,
      this.leader.position.y,
      this.exit.position.x,
      this.exit.position.y,
      (path) => {
        if (path === null) {
          // exit was placed into an unreachable tile.
          // flag for patch
          this.shouldRestart = true;
        } else {
          this.solvingPath = path.slice(1);
          this.leader.personalSpace = 10;
        }
      }
    );
    this.easystar.calculate();
    // =======================

    scene.events.once("levelstart", () => {
      if (this.shouldRestart) {
        // exit was placed into an unreachable tile.
        // restart as patch
        scene.levels.restart();
      } else if (this.unfinished) {
        if(this.totalShroomsAmount > 0) {
          this.showDialog1();
        } else {
          this.showDialog1_no_shrooms();
        }

        // reduce personal space after half way to exit
        const checkProgress = () => {
          if (this.leader.leadingPath.length < this.solvingPath.length / 3) {
            this.leader.personalSpace = 2;

            // drops an interactive object 3 blocks away from doc
            const threePositionsBackInPath = this.solvingPath.at(
              -this.leader.leadingPath.length - 4
            );
            const intElement = new Interactive(PIXEL_NAME.INTERACTION, this);
            intElement.order = 9;
            const { x, y } = threePositionsBackInPath;
            this.interactiveElements[`${x}-${y}`] = intElement;
            intElement.setPosition({ x, y });
          } else {
            scene.events.once("player_moves", checkProgress);
          }
        };
        scene.events.once("player_moves", checkProgress);
      }
    });

    // fix scenario if it was finished once at least
    if (!this.unfinished) {
      // plaster the wall
      const layer = this.scene.children.getByName("layer_1");
      const debris = new Debris(PIXEL_NAME.DEBRIS, this);
      debris.setPosition({
        x: this.leader.position.x - 1,
        y: this.leader.position.y,
      });
      layer.copy(0, 4, 1, 1, 2, 5);
      this.setObjectAt(
        this.leader.position.x + 1,
        this.leader.position.y,
        debris
      );

      // remove dwarf
      this.replaceByEmpty(this.leader);
      this.leader.alien.destroy();
      this.leader.alien = null;
    }
  }

  get id() {
    return this.constructor.key;
  }

  onInteractWith(el) {
    super.onInteractWith(el);
    if (el.order === 0) {
      this.onComplete();
    }

    if(el.order === 9 && this.buyLeadingWithShrooms) {
      // dwarf reduced personal space
      this.showDialog2()
    }
  }

  onAddElement(el) {
    if (el.isClass(Player)) {
      el.power = 1;
      el.hasPickaxe = false;
    }

    if (el.isClass(Exit)) {
      const layer = this.scene.children.getByName("layer_2");
      const map = layer.tilemap;

      this.exitEye = this.scene.add.sprite(48, 16, "spark").setOrigin(0.5, 0.5);
      this.exitEye.setTintFill(0xd25400);
      this.exitEye.setScale(layer.scaleX * 2, layer.scaleX * 4);
      this.exitEye.x = map.tileToWorldX(el.position.x - 0.5);
      this.exitEye.y = map.tileToWorldY(el.position.y - 0.4);
      this.exitEye.depth = 100;
      this.exitEye.alpha = 0;
    }
  }

  showDialog1_no_shrooms() {
    this.scene.events.once("dialogue:hidden", () => {
      this.leader.leadingPath = [
        { x: this.leader.position.x + 1, y: this.leader.position.y },
      ];
      this.setUpLeader();
    });

    this.scene.events.emit("dialogue:show", {
      participants: ["closeUp_Mary", "closeUp_Dwarf"],
      participantNames: ["Mary", "Doc"],
      conversation: [
        {
          who: "closeUp_Dwarf",
          text: [translate("maze_1.dialog_1.dwarf_say_1")],
        },
        {
          who: "closeUp_Mary",
          text: [translate("maze_1.dialog_1.mary_say_2_empty")],
        },
        {
          who: "closeUp_Dwarf",
          text: [template(translate("maze_1.dialog_1.dwarf_say_2_empty"))({ NUM: 9})],
        },
      ]
    })
  }
  showDialog1() {
    this.scene.events.once("dialogue:hidden", () => {
      if (this.buyLeadingWithShrooms) {
        this.scene.events.emit("score:mushroom_given");
        this.collectedShrooms--;

        this.leader.leadingPath = [...this.solvingPath];
      } else {
        this.leader.leadingPath = [
          { x: this.leader.position.x + 1, y: this.leader.position.y },
        ];
      }
      this.setUpLeader();
    });
    this.scene.events.emit("dialogue:show", {
      participants: ["closeUp_Mary", "closeUp_Dwarf"],
      participantNames: ["Mary", "Doc"],
      conversation: [
        {
          who: "closeUp_Dwarf",
          text: [translate("maze_1.dialog_1.dwarf_say_1")],
        },
        {
          who: "closeUp_Mary",
          text: [translate("maze_1.dialog_1.mary_say_1")],
          options: {
            slot0: {
              label: translate("maze_1.dialog_1_option.yes"),
              slot: 0,
              onSelect: () => {
                this.buyLeadingWithShrooms = true;
                this.scene.events.emit("dialogue:nextPage");
              },
            },
            slot1: {
              label: translate("maze_1.dialog_1_option.no"),
              slot: 1,
              onSelect: () => this.scene.events.emit("dialogue:nextPage", 1),
            },
            slot2: null,
            slot3: null,
          },
        },
        {
          who: "closeUp_Mary",
          text: [translate("maze_1.dialog_1.mary_say_2_yes")],
          options: {
            slot2: null, // hide default NEXT option
          },
        },
        {
          who: "closeUp_Mary",
          text: [translate("maze_1.dialog_1.mary_say_2_no")],
        },
      ],
    });
  }

  setUpLeader() {
    this.player.disconnect();
    this.dwarfRun = true;
    this.moveDwarf();
  }

  moveDwarf() {
    // auto moves dwarf until gets personal space
    if (this.dwarfRun) {
      if (
        !this.leader.isCloseEnough(this.player.position) ||
        this.leader.leadingPath.length === 0
      ) {
        this.dwarfRun = false;
      }

      this.onNextTick(() => {
        // hack: force player move action
        this.leader.onPlayerMoves(this.player.position);
        this.moveDwarf();
      });
    } else {
      this.leader.animIdle();
      this.player.reconnect();
    }
  }

  showDialog2() {
    this.scene.events.emit("dialogue:show", {
      participants: ["closeUp_Mary", "closeUp_Dwarf"],
      participantNames: ["Mary", "Doc"],
      conversation: [
        {
          who: "closeUp_Mary",
          text: [translate("maze_1.dialog_2.mary_say_1")],
        },
        {
          who: "closeUp_Dwarf",
          text: [translate("maze_1.dialog_2.dwarf_say_1")],
        },
      ],
    });
  }
}

LevelMaze1.key = "maze_1";
export default LevelMaze1;
